"use strict";
// Copyright (C) 2017-2026 Smart code 203358507
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = __importStar(require("react"));
var react_i18next_1 = require("react-i18next");
var useToast_1 = __importDefault(require("stremio/common/Toast/useToast"));
var GamepadContext_1 = __importDefault(require("./GamepadContext"));
var detectControllerType = function (gamepad) {
    var id = gamepad.id.toLowerCase();
    // Sony vendor id 054c — DualShock / DualSense / generic PlayStation
    if (/sony|playstation|dualsense|dualshock|054c/.test(id))
        return 'playstation';
    // Microsoft vendor id 045e — Xbox / XInput
    if (/xbox|microsoft|xinput|045e/.test(id))
        return 'xbox';
    // Browser "Standard Gamepad" mapping mirrors the Xbox layout
    if (gamepad.mapping === 'standard')
        return 'xbox';
    return 'generic';
};
var GamepadProvider = function (_a) {
    var enabled = _a.enabled, onGuide = _a.onGuide, children = _a.children;
    var t = (0, react_i18next_1.useTranslation)().t;
    var toast = (0, useToast_1.default)();
    var connectedGamepads = (0, react_1.useRef)(0);
    var lastButtonState = (0, react_1.useRef)([]);
    var lastButtonPressedTime = (0, react_1.useRef)(0);
    var axisTimer = (0, react_1.useRef)(0);
    var axisTimerRight = (0, react_1.useRef)(0);
    var eventHandlers = (0, react_1.useRef)(new Map());
    var lockPrefix = (0, react_1.useRef)(null);
    var _b = (0, react_1.useState)('generic'), controllerType = _b[0], setControllerType = _b[1];
    var on = (0, react_1.useCallback)(function (event, id, callback) {
        if (!eventHandlers.current.has(event)) {
            eventHandlers.current.set(event, new Map());
        }
        var handlers = eventHandlers.current.get(event);
        // Ensure only one handler per component
        handlers.set(id, callback);
    }, []);
    var off = (0, react_1.useCallback)(function (event, id) {
        var handlersMap = eventHandlers.current.get(event);
        handlersMap === null || handlersMap === void 0 ? void 0 : handlersMap.delete(id);
        if ((handlersMap === null || handlersMap === void 0 ? void 0 : handlersMap.size) === 0) {
            eventHandlers.current.delete(event);
        }
    }, []);
    var lock = (0, react_1.useCallback)(function (prefix) {
        lockPrefix.current = prefix;
    }, []);
    var unlock = (0, react_1.useCallback)(function () {
        lockPrefix.current = null;
    }, []);
    var emit = function (event, data) {
        if (eventHandlers.current.has(event)) {
            var handlersMap = eventHandlers.current.get(event);
            if (!handlersMap || handlersMap.size === 0)
                return;
            if (lockPrefix.current) {
                var matching = Array.from(handlersMap.entries())
                    .filter(function (_a) {
                    var id = _a[0];
                    return id.startsWith(lockPrefix.current);
                });
                if (matching.length > 0) {
                    matching[matching.length - 1][1](data);
                }
                return;
            }
            var latestHandler = Array.from(handlersMap.values()).slice(-1)[0];
            if (latestHandler) {
                latestHandler(data);
            }
        }
    };
    var onGamepadConnected = (0, react_1.useCallback)(function (e) {
        setControllerType(detectControllerType(e.gamepad));
        // @ts-expect-error show() expects no arguments
        toast.show({
            type: 'info',
            title: t('GAMEPAD_CONNECTED'),
            timeout: 4000,
        });
    }, [toast, t]);
    var onGamepadDisconnected = (0, react_1.useCallback)(function () {
        var remaining = Array.from(navigator.getGamepads()).filter(function (gp) { return gp !== null; });
        setControllerType(remaining.length > 0 ? detectControllerType(remaining[0]) : 'generic');
        // @ts-expect-error show() expects no arguments
        toast.show({
            type: 'info',
            title: t('GAMEPAD_DISCONNECTED'),
            timeout: 4000,
        });
    }, [toast, t]);
    (0, react_1.useEffect)(function () {
        if (!enabled)
            return;
        if (typeof navigator.getGamepads === 'function') {
            var existing = Array.from(navigator.getGamepads()).filter(function (gp) { return gp !== null; });
            if (existing.length > 0) {
                setControllerType(detectControllerType(existing[0]));
            }
        }
        window.addEventListener('gamepadconnected', onGamepadConnected);
        window.addEventListener('gamepaddisconnected', onGamepadDisconnected);
        return function () {
            window.removeEventListener('gamepadconnected', onGamepadConnected);
            window.removeEventListener('gamepaddisconnected', onGamepadDisconnected);
        };
    }, [enabled, onGamepadConnected, onGamepadDisconnected]);
    (0, react_1.useEffect)(function () {
        if (onGuide) {
            on('buttonX', 'guide', onGuide);
        }
        return function () {
            off('buttonX', 'guide');
        };
    }, [onGuide]);
    (0, react_1.useEffect)(function () {
        if (!enabled || typeof navigator.getGamepads !== 'function')
            return;
        var animationFrameId;
        var updateStatus = function () {
            if (document.hasFocus()) {
                var currentTime_1 = Date.now();
                var controllers = Array.from(navigator.getGamepads()).filter(function (gp) { return gp !== null; });
                connectedGamepads.current = controllers.length;
                controllers.forEach(function (controller, index) {
                    var buttonsState = controller.buttons.reduce(function (buttons, button, i) { return buttons | (button.pressed ? 1 << i : 0); }, 0);
                    var processButton = currentTime_1 - lastButtonPressedTime.current > 250;
                    if (lastButtonState.current[index] !== buttonsState ||
                        processButton) {
                        lastButtonPressedTime.current = currentTime_1;
                        lastButtonState.current[index] = buttonsState;
                        if (buttonsState & (1 << 0))
                            emit('buttonA');
                        if (buttonsState & (1 << 1))
                            emit('buttonB');
                        if (buttonsState & (1 << 2))
                            emit('buttonX');
                        if (buttonsState & (1 << 3))
                            emit('buttonY');
                        if (buttonsState & (1 << 4))
                            emit('buttonLT');
                        if (buttonsState & (1 << 5))
                            emit('buttonRT');
                    }
                    var deadZone = 0.05;
                    var maxSpeed = 100;
                    var axisHandled = false;
                    if (controller.axes[0] < -deadZone) {
                        if (currentTime_1 - axisTimer.current >
                            maxSpeed + (2000 - Math.abs(controller.axes[0]) * 2000)) {
                            emit('analog', 'left');
                            axisHandled = true;
                        }
                    }
                    if (controller.axes[0] > deadZone) {
                        if (currentTime_1 - axisTimer.current >
                            maxSpeed + (2000 - Math.abs(controller.axes[0]) * 2000)) {
                            emit('analog', 'right');
                            axisHandled = true;
                        }
                    }
                    if (controller.axes[1] < -deadZone) {
                        if (currentTime_1 - axisTimer.current >
                            maxSpeed + (2000 - Math.abs(controller.axes[1]) * 2000)) {
                            emit('analog', 'up');
                            axisHandled = true;
                        }
                    }
                    if (controller.axes[1] > deadZone) {
                        if (currentTime_1 - axisTimer.current >
                            maxSpeed + (2000 - Math.abs(controller.axes[1]) * 2000)) {
                            emit('analog', 'down');
                            axisHandled = true;
                        }
                    }
                    if (axisHandled)
                        axisTimer.current = currentTime_1;
                    var rightAxisHandled = false;
                    if (controller.axes.length > 2) {
                        if (controller.axes[2] < -deadZone) {
                            if (currentTime_1 - axisTimerRight.current > maxSpeed + (2000 - Math.abs(controller.axes[2]) * 2000)) {
                                emit('analogRight', 'left');
                                rightAxisHandled = true;
                            }
                        }
                        if (controller.axes[2] > deadZone) {
                            if (currentTime_1 - axisTimerRight.current > maxSpeed + (2000 - Math.abs(controller.axes[2]) * 2000)) {
                                emit('analogRight', 'right');
                                rightAxisHandled = true;
                            }
                        }
                        if (controller.axes[3] < -deadZone) {
                            if (currentTime_1 - axisTimerRight.current > maxSpeed + (2000 - Math.abs(controller.axes[3]) * 2000)) {
                                emit('analogRight', 'up');
                                rightAxisHandled = true;
                            }
                        }
                        if (controller.axes[3] > deadZone) {
                            if (currentTime_1 - axisTimerRight.current > maxSpeed + (2000 - Math.abs(controller.axes[3]) * 2000)) {
                                emit('analogRight', 'down');
                                rightAxisHandled = true;
                            }
                        }
                    }
                    if (rightAxisHandled)
                        axisTimerRight.current = currentTime_1;
                });
            }
            animationFrameId = requestAnimationFrame(updateStatus);
        };
        animationFrameId = requestAnimationFrame(updateStatus);
        return function () {
            cancelAnimationFrame(animationFrameId);
        };
    }, [enabled]);
    return (react_1.default.createElement(GamepadContext_1.default.Provider, { value: { on: on, off: off, lock: lock, unlock: unlock, controllerType: controllerType } }, children));
};
exports.default = GamepadProvider;
